import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress
} from '@material-ui/core';

import { MODAL_TYPE, useModals } from '../../../../hooks/useModal';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import AdminHeader from '../../../../components/atom/AdminHeader';
import TablePager from '../../../../components/atom/TablePager';
import { DeleteIcon, EditIcon } from '../../../../icons';
import ActionIconButton from '../../../../components/atom/ActionIconButton';
import { formatMoney } from '../../../../utils';

function discountAmountFormat({ amount, type }) {
    const floatAmount = parseFloat(amount);
    if (floatAmount === 0 || isNaN(floatAmount) || type === '') {
        // hide 0 amounts
        return '';
    }
    if (type === '$') {
        return formatMoney(floatAmount);
    }
    return `${floatAmount} %`;
}

function DiscountValueCell({ discount }) {
    const agency = discountAmountFormat(discount.agencyDiscount);
    const vendor = discountAmountFormat(discount.vendorDiscount);
    return (
        <TableCell>
            {agency !== '' && `Agency discount: ${agency}`}
            {agency !== '' && vendor !== '' && <br />}
            {vendor !== '' && `Vendor discount: ${vendor}`}
        </TableCell>
    );
}

function DiscountPackageListPage({ clientId }) {
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();

    const [data, loading, error, setData, reloadDiscountPackages] = useSwaggerClient(
        'discountpackages',
        'listDiscountPackagesPerClient',
        {
            clientId,
            page,
            pageSize,
            search
        },
        [page, pageSize, search]
    );
    // wire error to a notification
    useEffect(() => {
        if (error) {
            notifyError(error.message);
        }
    }, [error, notifyError]);

    const deleteDiscountPackage = useCallback(
        discountPackageId => {
            doSwaggerCall('discountpackages', 'deleteDiscountPackageById', { discountPackageId })
                .then(() => {
                    // we finished, remove discountPackage from list
                    notifySuccess('DiscountPackage deleted');
                    reloadDiscountPackages();
                })
                .catch(() => {
                    notifyError('Cannot delete discount package');
                });
        },
        [data, setData, notifyError, notifySuccess, reloadDiscountPackages]
    );

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <AdminHeader
                title="Discount Packages"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
                createLabel="New discount package"
                onCreate={() => {
                    showModal(MODAL_TYPE.CREATE_DISCOUNTPACKAGE, {
                        clientId
                    });
                }}
            />
            <Grid item container xs={12} justifyContent="center">
                {loading && data?.discountPackages.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <Table style={{ backgroundColor: 'white' }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Timeshare</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data &&
                                data.discountPackages &&
                                data.discountPackages.map(discountPackage => (
                                    <TableRow key={discountPackage.id}>
                                        <TableCell>{discountPackage.name}</TableCell>
                                        <DiscountValueCell discount={discountPackage?.discount} />
                                        <TableCell>
                                            {discountPackage.wholecart ? 'Whole cart' : 'Per item'}
                                        </TableCell>
                                        <TableCell>
                                            {discountPackage.timeshareactivity_id ? 'Yes' : 'No'}
                                        </TableCell>
                                        <TableCell style={{ width: 100 }}>
                                            <ActionIconButton
                                                title="Edit"
                                                icon={EditIcon}
                                                to={`/admin/client/${clientId}/discountpackages/${discountPackage.id}`}
                                            />
                                            <ActionIconButton
                                                title="Delete"
                                                icon={DeleteIcon}
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: `Are you sure you want to remove ${discountPackage?.name}?`,
                                                        content: 'This action is irreversible.',
                                                        confirm: () =>
                                                            deleteDiscountPackage(discountPackage.id)
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={data?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default DiscountPackageListPage;
